import barba from '@barba/core';
import { SoundEffect, playSound } from './soundEffects';
import { getLenis, stopLenis, resumeLenis, resizeLenis } from './lenis';

/**
 * Initialize Barba.js page transitions
 */
export const initBarba = (): void => {
    barba.init({
        transitions: [
            {
                name: 'fade-transition',
                leave(data) {
                    return new Promise<void>((resolve) => {
                        // Stop Lenis during transition
                        stopLenis();

                        // Play transition sound
                        playSound(SoundEffect.TRANSITION, 0.3);

                        // Fade out animation
                        const tl = fadeOut(data.current.container);
                        tl.then(() => resolve());
                    });
                },
                enter(data) {
                    return new Promise<void>((resolve) => {
                        // Set initial state
                        data.next.container.style.opacity = '0';
                        data.next.container.style.transform = 'translateY(20px)';

                        // Fade in animation
                        const tl = fadeIn(data.next.container);
                        tl.then(() => {
                            // Resume Lenis and resize
                            resumeLenis();
                            resizeLenis();
                            resolve();
                        });
                    });
                }
            },
            {
                name: 'slide-transition',
                to: {
                    custom: ({ trigger }) => {
                        return trigger.dataset.transition === 'slide';
                    }
                },
                leave(data) {
                    return new Promise<void>((resolve) => {
                        stopLenis();
                        playSound(SoundEffect.TRANSITION, 0.3);

                        const tl = slideOut(data.current.container);
                        tl.then(() => resolve());
                    });
                },
                enter(data) {
                    return new Promise<void>((resolve) => {
                        data.next.container.style.transform = 'translateX(100%)';

                        const tl = slideIn(data.next.container);
                        tl.then(() => {
                            resumeLenis();
                            resizeLenis();
                            resolve();
                        });
                    });
                }
            }
        ],
        views: [
            {
                namespace: 'home',
                beforeEnter() {
                    // Preload critical resources for home page
                    console.log('Entering home page');
                }
            },
            {
                namespace: 'project',
                beforeEnter() {
                    // Initialize project-specific functionality
                    console.log('Entering project page');
                }
            }
        ]
    });

    // Global hooks
    barba.hooks.before(() => {
        // Scroll to top before transition
        window.scrollTo(0, 0);
    });

    barba.hooks.after(() => {
        // Play success sound after transition
        setTimeout(() => {
            playSound(SoundEffect.SUCCESS, 0.2);
        }, 300);
    });

    barba.hooks.beforeEnter(() => {
        // Ensure proper cleanup
        const loadingScreens = document.querySelectorAll('[data-loading]');
        loadingScreens.forEach(screen => screen.remove());
    });
};

/**
 * Fade out animation
 */
const fadeOut = (element: HTMLElement): Promise<void> => {
    return new Promise((resolve) => {
        element.style.transition = 'opacity 0.5s ease-out, transform 0.5s ease-out';
        element.style.opacity = '0';
        element.style.transform = 'translateY(-20px)';

        setTimeout(() => {
            resolve();
        }, 500);
    });
};

/**
 * Fade in animation
 */
const fadeIn = (element: HTMLElement): Promise<void> => {
    return new Promise((resolve) => {
        element.style.transition = 'opacity 0.6s ease-in, transform 0.6s ease-in';

        // Force reflow
        element.offsetHeight;

        element.style.opacity = '1';
        element.style.transform = 'translateY(0)';

        setTimeout(() => {
            // Clear transition styles
            element.style.transition = '';
            resolve();
        }, 600);
    });
};

/**
 * Slide out animation
 */
const slideOut = (element: HTMLElement): Promise<void> => {
    return new Promise((resolve) => {
        element.style.transition = 'transform 0.7s cubic-bezier(0.645, 0.045, 0.355, 1)';
        element.style.transform = 'translateX(-100%)';

        setTimeout(() => {
            resolve();
        }, 700);
    });
};

/**
 * Slide in animation
 */
const slideIn = (element: HTMLElement): Promise<void> => {
    return new Promise((resolve) => {
        element.style.transition = 'transform 0.7s cubic-bezier(0.645, 0.045, 0.355, 1)';

        // Force reflow
        element.offsetHeight;

        element.style.transform = 'translateX(0)';

        setTimeout(() => {
            // Clear transition styles
            element.style.transition = '';
            resolve();
        }, 700);
    });
};

/**
 * Manually trigger a transition to a specific URL
 */
export const goTo = (url: string, transition = 'fade'): void => {
    const link = document.createElement('a');
    link.href = url;
    link.dataset.transition = transition;
    barba.go(url, { trigger: link });
};
